import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { siteConfig, categories } from '@/mocks/siteData';
import type { ArticleContent } from '@/mocks/articleData';

interface ArticleLayoutProps {
  article: ArticleContent;
}

export default function ArticleLayout({ article }: ArticleLayoutProps) {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const steps = [
    { num: 1, title: 'Product Discovery', desc: 'Browse product categories through the mulebuy spreadsheet catalog. Each category contains curated listings with detailed product information.' },
    { num: 2, title: 'Data Import to Spreadsheet', desc: 'Product data is imported into the mulebuy spreadsheet format, including SKU numbers, pricing tiers, size availability, and seller ratings.' },
    { num: 3, title: 'Side-by-Side Comparison', desc: 'Use the mulebuy spreadsheet comparison view to evaluate products side by side based on price, quality indicators, and shipping speed.' },
    { num: 4, title: 'Order Placement & Tracking', desc: 'Once you have selected your items, the mulebuy spreadsheet system streamlines the ordering process and provides real-time tracking updates.' },
  ];

  return (
    <div className="min-h-screen bg-background-50">
      <main>
        <article className="pt-24 pb-16 md:pb-24">
          <div className="px-4 md:px-6 max-w-3xl mx-auto">
            <nav className="flex items-center gap-2 text-xs text-foreground-500 mb-8 flex-wrap">
              <Link to="/" className="hover:text-primary-500 transition-colors cursor-pointer">
                Home
              </Link>
              <i className="ri-arrow-right-s-line text-xs"></i>
              <span className="text-foreground-700">{article.title}</span>
            </nav>

            <header className="mb-10">
              <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-950 leading-tight mb-4">
                {article.title}
              </h1>
              <div className="flex items-center gap-4 text-xs text-foreground-500 mb-6 flex-wrap">
                <span>{article.date}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400 hidden sm:block"></span>
                <span>{article.readTime}</span>
                <span className="w-1 h-1 rounded-full bg-foreground-400 hidden sm:block"></span>
                <span>Mulebuy Spreadsheet Research Team</span>
              </div>
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full rounded-xl mb-10"
                title={article.title}
              />
            </header>

            <section className="mb-10">
              <p className="text-base md:text-lg text-foreground-700 leading-relaxed mb-5">
                {article.description}
              </p>
            </section>

            {article.sections.map((section, idx) => (
              <section key={idx} className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  {section.heading}
                </h2>
                {section.paragraphs.map((para, pidx) => (
                  <p key={pidx} className="text-base text-foreground-700 leading-relaxed mb-4">
                    {para}
                  </p>
                ))}

                {section.heading.includes('Step-by-Step') && (
                  <div className="space-y-4 mt-4">
                    {steps.map((step) => (
                      <div key={step.num} className="flex gap-4">
                        <div className="w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                          <span className="text-xs font-bold text-primary-600">{step.num}</span>
                        </div>
                        <div>
                          <h4 className="text-sm font-semibold text-foreground-950 mb-1">{step.title}</h4>
                          <p className="text-sm text-foreground-600">{step.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </section>
            ))}

            {article.slug === 'mulebuy-spreadsheet-complete-guide' && (
              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  Mulebuy Spreadsheet vs Agent Shopping: Full Comparison
                </h2>
                <div className="overflow-x-auto rounded-lg border border-background-200">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-background-100">
                        <th className="text-left px-4 py-3 font-semibold text-foreground-950">Feature</th>
                        <th className="text-left px-4 py-3 font-semibold text-primary-600">Mulebuy Spreadsheet</th>
                        <th className="text-left px-4 py-3 font-semibold text-foreground-600">Traditional Agent Shopping</th>
                      </tr>
                    </thead>
                    <tbody>
                      {[
                        ['Product Comparison Speed', 'Instant side-by-side view', 'Manual tab switching'],
                        ['Pricing Transparency', 'Full fee breakdown visible', 'Hidden fees common'],
                        ['Order Tracking', 'Spreadsheet-integrated tracking', 'Separate tracking per order'],
                        ['Bulk Order Management', 'Single spreadsheet view', 'Multiple separate orders'],
                        ['Learning Curve', 'Moderate — guide recommended', 'Low but inefficient'],
                        ['Data Export Options', 'CSV, Excel, Google Sheets', 'Limited or none'],
                      ].map((row, i) => (
                        <tr key={i} className={`border-t border-background-200 ${i % 2 === 1 ? 'bg-background-100/50' : ''}`}>
                          <td className="px-4 py-3 font-medium text-foreground-800">{row[0]}</td>
                          <td className="px-4 py-3 text-primary-600">{row[1]}</td>
                          <td className="px-4 py-3 text-foreground-600">{row[2]}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </section>
            )}

            {article.slug === 'mulebuy-spreadsheet-complete-guide' && (
              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  Mulebuy Spreadsheet Review: Pros and Cons
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-accent-50 border border-accent-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-accent-700 mb-3 flex items-center gap-2">
                      <i className="ri-check-line"></i> Pros
                    </h3>
                    <ul className="space-y-2 text-sm text-foreground-700">
                      <li className="flex items-start gap-2"><span className="text-accent-500 mt-0.5">&#10003;</span> Massive time savings with spreadsheet-based comparison</li>
                      <li className="flex items-start gap-2"><span className="text-accent-500 mt-0.5">&#10003;</span> Full pricing transparency with no hidden costs</li>
                      <li className="flex items-start gap-2"><span className="text-accent-500 mt-0.5">&#10003;</span> Supports bulk orders across multiple product categories</li>
                      <li className="flex items-start gap-2"><span className="text-accent-500 mt-0.5">&#10003;</span> Real-time order tracking integrated into the spreadsheet</li>
                      <li className="flex items-start gap-2"><span className="text-accent-500 mt-0.5">&#10003;</span> Exportable data for offline analysis and record-keeping</li>
                    </ul>
                  </div>
                  <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-5">
                    <h3 className="text-sm font-semibold text-secondary-700 mb-3 flex items-center gap-2">
                      <i className="ri-close-line"></i> Cons
                    </h3>
                    <ul className="space-y-2 text-sm text-foreground-700">
                      <li className="flex items-start gap-2"><span className="text-secondary-500 mt-0.5">&#10007;</span> Requires learning the mulebuy spreadsheet interface</li>
                      <li className="flex items-start gap-2"><span className="text-secondary-500 mt-0.5">&#10007;</span> Not all product categories may be available at all times</li>
                      <li className="flex items-start gap-2"><span className="text-secondary-500 mt-0.5">&#10007;</span> Dependent on third-party agent services for fulfillment</li>
                      <li className="flex items-start gap-2"><span className="text-secondary-500 mt-0.5">&#10007;</span> May have regional restrictions for certain product types</li>
                    </ul>
                  </div>
                </div>
              </section>
            )}

            {article.faqs.length > 0 && (
              <section className="mb-10">
                <h2 className="text-xl md:text-2xl font-heading font-semibold text-foreground-950 mb-4">
                  Frequently Asked Questions
                </h2>
                <div className="space-y-3">
                  {article.faqs.map((faq, idx) => (
                    <div key={idx} className="border border-background-200 rounded-lg p-4">
                      <h3 className="text-sm font-semibold text-foreground-950 mb-2">{faq.question}</h3>
                      <p className="text-sm text-foreground-600">{faq.answer}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            <section className="mb-10 bg-primary-50 border border-primary-200 rounded-xl p-6 md:p-8 text-center">
              <h2 className="text-lg md:text-xl font-heading font-semibold text-foreground-950 mb-3">
                Ready to Try Mulebuy Spreadsheet?
              </h2>
              <p className="text-sm text-foreground-700 mb-5 max-w-lg mx-auto">
                Explore product categories and start using the mulebuy spreadsheet system today.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
                <Link
                  to="/"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md font-semibold text-sm hover:bg-primary-600 transition-all whitespace-nowrap cursor-pointer"
                >
                  Back to Mulebuy Spreadsheet Hub
                </Link>
                <a
                  href={siteConfig.mainSite}
                  target="_blank"
                  rel="noopener nofollow"
                  className="px-6 py-2.5 border border-primary-300 text-primary-600 rounded-md font-semibold text-sm hover:bg-primary-100 transition-all whitespace-nowrap cursor-pointer"
                >
                  Browse Products Now
                </a>
              </div>
            </section>

            <section>
              <h3 className="text-sm font-semibold text-foreground-950 mb-4">
                Explore Mulebuy Spreadsheet Product Categories
              </h3>
              <div className="flex flex-wrap gap-2">
                {categories.slice(0, 8).map((cat) => (
                  <a
                    key={cat.seq}
                    href={cat.url}
                    target="_blank"
                    rel="noopener nofollow"
                    className="px-3 py-1.5 text-xs bg-background-100 text-foreground-600 rounded-full hover:bg-primary-100 hover:text-primary-600 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    {cat.name}
                  </a>
                ))}
              </div>
            </section>
          </div>
        </article>
      </main>
    </div>
  );
}